import { inject, injectable, injectFromBase } from 'inversify'
import deepmerge from 'deepmerge'
import type BaseWrapper from '@/data/models/wrappers/BaseWrapper'
import { SERVICE_TYPES } from '@/core/container/types'
import type LoggerService from '@/services/loggerService'
import TableBaseStorageWrapperService from '@/services/wrappers/table/tableBaseStorageWrapperService'
import type BaseWrapperService from '@/services/wrappers/baseWrapperService'
import type { LocalStorageServiceInterface } from '@/services/storage/types'
import type UserService from '@/services/domain/user/userService'
import type { WrapperTableParams } from '@/services/wrappers/table/types'
import { isWrapperTableParams } from '@/services/wrappers/table/types'
import type { WrapperParams } from '@/services/wrappers/types'
import { isRecordUnknown } from '@/utils/typeGuards'
import { isNumberString } from '@/utils/typeGuards/isNumberString'

@injectable()
@injectFromBase()
export default class TableLocalStorageWrapperService extends TableBaseStorageWrapperService {
  constructor(
    @inject(SERVICE_TYPES.BaseWrapperService) protected readonly baseWrapperService: BaseWrapperService<BaseWrapper>,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.LocalStorageService) protected readonly storageService: LocalStorageServiceInterface,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {
    super(baseWrapperService, loggerService, storageService)
  }

  public override getStorageParams(wrapperId: string): WrapperParams {
    const params = super.getStorageParams(wrapperId)
    const currentUserId = this.userService.currentUserId()
    if (!currentUserId) {
      return params
    }

    if (
      isRecordUnknown(params) &&
      Object.entries(params).some(([key, value]) => isNumberString(key) && isWrapperTableParams(value))
    ) {
      return params[String(currentUserId)] || {}
    }

    return {}
  }

  public override setStorageParams(wrapperId: string, params: WrapperTableParams, shouldReplaceState = true): void {
    const currentUserId = this.userService.currentUserId()
    if (!currentUserId) {
      // If no userId, go default flow
      return super.setStorageParams(wrapperId, params, shouldReplaceState)
    }

    // Get existing data to preserve other users' data
    const existingParams = super.getStorageParams(wrapperId) || {}

    // Update only the current user's data
    const updatedParams = deepmerge(
      existingParams,
      { [String(currentUserId)]: params },
      {
        customMerge: (key, options) => {
          // Special case for merging user-specific table params
          if (isNumberString(key)) {
            return (oldVal, newVal) => {
              if (isWrapperTableParams(newVal)) {
                if (shouldReplaceState) {
                  return newVal
                }

                // Do not merge deeply, just replace the existing keys with new ones
                return {
                  ...(oldVal ?? {}),
                  ...newVal,
                }
              }

              // Act using default behavior
              return undefined
            }
          }

          // Act using default behavior
          return undefined
        },
        arrayMerge: (dst, src) => {
          // do not merge arrays, just replace them
          return src
        },
      },
    )

    return super.setStorageParams(wrapperId, updatedParams, shouldReplaceState)
  }
}
